/**
 * Guests — people from outside the organisation, invited to specific courses.
 *
 * A guest has no Mauvely account. They get a link; opening it signs them in to
 * this organisation's Learn and to the courses named here, nothing else. There
 * is no mail sender on this deployment yet, so the link is shown to the
 * inviter to send themselves, with the day it stops opening.
 *
 * A teacher sees the guests they invited; an admin sees everyone's. The same
 * email again is the same guest, given the new courses and a new link.
 */

import { useMemo, useState } from 'react';
import type { ExternalLearnerWire } from '@wire';
import { errorText, teach } from '../api';
import { ErrorNote, Footnote, Loading, OkNote, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { ds } from '../ds';
import { HAIR, formatDeadline, monoPlain, plural } from '../theme';
import { useLoad } from '../useLoad';

/** End of the chosen local day, as the assign dialog does it. */
const endOfDay = (value: string): number | null => {
  if (!value) return null;
  const d = new Date(`${value}T12:00:00`);
  return Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).getTime() / 1000);
};

function CopyLink({ link, expiresAt }: { link: string; expiresAt: number }) {
  const { Button } = ds();
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code style={{ ...monoPlain, letterSpacing: 0, textTransform: 'none', overflowWrap: 'anywhere', flex: '1 1 260px' }}>
          {link}
        </code>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            void navigator.clipboard.writeText(link).then(
              () => setCopied(true),
              () => setCopied(false)
            )
          }
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </div>
      <Footnote>{`Opens until ${formatDeadline(expiresAt)}.`}</Footnote>
    </div>
  );
}

export default function Guests() {
  const { Badge, Button, Checkbox, Input } = ds();

  const guests = useLoad(() => teach.externals.list(), 'Could not load guests.');
  const courses = useLoad(() => teach.courses(), 'Could not load your courses.');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [due, setDue] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ who: string; link: string; expiresAt: number } | null>(null);

  const live = useMemo(
    () => (courses.data?.courses ?? []).filter((r) => r.card.state === 'live').map((r) => r.card),
    [courses.data]
  );

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await guests.reload();
    } catch (err) {
      setError(errorText(err, 'That did not work.'));
    }
    setBusy(false);
  };

  const invite = () =>
    run(async () => {
      const result = await teach.externals.invite({
        name: name.trim(),
        email: email.trim(),
        courseIds: chosen,
        dueAt: endOfDay(due)
      });
      setIssued({ who: result.external.name, link: result.link, expiresAt: result.expiresAt });
      setName('');
      setEmail('');
      setChosen([]);
      setDue('');
    });

  const newLink = (g: ExternalLearnerWire) =>
    run(async () => {
      const result = await teach.externals.link(g.id);
      setIssued({ who: g.name, link: result.link, expiresAt: result.expiresAt });
    });

  const withdraw = (g: ExternalLearnerWire) =>
    run(async () => {
      await teach.externals.revoke(g.id);
      setIssued(null);
    });

  if (guests.error) return <ErrorNote>{guests.error}</ErrorNote>;
  if (!guests.data) return guests.loading ? <Loading /> : null;

  const list = guests.data.externals;
  const ready = name.trim() && email.trim() && chosen.length > 0 && !busy;

  return (
    <>
      <PageTitle title="Guests." meta={plural(list.length, 'guest')} />

      <ErrorNote>{error}</ErrorNote>
      {issued && (
        <Panel style={{ display: 'grid', gap: 10 }}>
          <OkNote>{`Send this link to ${issued.who}.`}</OkNote>
          <CopyLink link={issued.link} expiresAt={issued.expiresAt} />
        </Panel>
      )}

      <Panel style={{ display: 'grid', gap: 16 }}>
        <PanelHead title="Invite someone" />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <Input label="Name" size="sm" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div style={{ flex: '1 1 240px' }}>
            <Input
              label="Email"
              type="email"
              size="sm"
              value={email}
              autoComplete="off"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>Courses</span>
          {courses.loading && !courses.data ? (
            <Loading />
          ) : live.length === 0 ? (
            <Footnote>Publish a course to invite anyone to it.</Footnote>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {live.map((c) => (
                <Checkbox
                  key={c.id}
                  label={c.title}
                  checked={chosen.includes(c.id)}
                  onChange={(on) =>
                    setChosen((now) => (on ? [...now, c.id] : now.filter((id) => id !== c.id)))
                  }
                />
              ))}
            </div>
          )}
        </div>

        <div style={{ maxWidth: 200 }}>
          <Input label="Deadline" type="date" size="sm" value={due} onChange={(e) => setDue(e.target.value)} />
        </div>

        <div>
          <Button size="sm" disabled={!ready} onClick={() => void invite()}>
            Invite
          </Button>
        </div>
      </Panel>

      {list.length > 0 && (
        <Panel style={{ display: 'grid', gap: 4 }}>
          <PanelHead title="Invited" meta={plural(list.length, 'guest')} />
          {list.map((g, i) => (
            <div
              key={g.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
                padding: '12px 0',
                borderTop: i === 0 ? 'none' : HAIR
              }}
            >
              <div style={{ display: 'grid', gap: 3, flex: '1 1 260px', minWidth: 0 }}>
                <span style={{ fontSize: 13.5 }}>
                  {g.name}
                  {g.revoked && (
                    <span style={{ marginLeft: 8 }}>
                      <Badge tone="neutral" mono>
                        Withdrawn
                      </Badge>
                    </span>
                  )}
                </span>
                <span style={{ ...monoPlain, letterSpacing: 0, textTransform: 'none' }}>{g.email}</span>
                {g.courses.length > 0 && (
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {g.courses.map((c) => c.title).join(' · ')}
                  </span>
                )}
                {g.courses.length > 0 && (
                  <span style={monoPlain}>
                    {`${g.courses.filter((c) => c.completedAt !== null).length} of ${g.courses.length} finished`}
                  </span>
                )}
              </div>
              {!g.revoked && (
                <span style={monoPlain}>
                  {g.linkExpiresAt === null
                    ? 'No live link'
                    : g.linkUsed
                      ? 'Signed in'
                      : `Link opens until ${formatDeadline(g.linkExpiresAt)}`}
                </span>
              )}
              {!g.revoked && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => void newLink(g)}>
                    New link
                  </Button>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => void withdraw(g)}>
                    Withdraw
                  </Button>
                </div>
              )}
            </div>
          ))}
        </Panel>
      )}
    </>
  );
}
